import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { studioInfo } from '../data/mock';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Mensaje Enviado",
          description: data.message || "Nos pondremos en contacto con usted a la brevedad.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast({
          title: "Error",
          description: data.detail || "Hubo un error al enviar el mensaje. Intente nuevamente.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        title: "Error",
        description: "No se pudo conectar con el servidor. Intente nuevamente más tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-20 bg-slate-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Contáctenos
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Estamos a su disposición para atender su consulta legal
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">
              Información de Contacto
            </h3>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Comuníquese con nosotros por cualquiera de los siguientes medios. 
              Respondemos consultas de lunes a viernes.
            </p>

            {/* Contact Methods */}
            <div className="space-y-6 mb-8">
              <a 
                href="tel:2236661214"
                className="flex items-start space-x-4 p-4 bg-slate-800 border border-slate-700 rounded-lg hover:border-gray-600 transition-colors group"
              >
                <div className="bg-gray-700 p-3 rounded-lg group-hover:bg-gray-600 transition-colors">
                  <Phone className="text-gray-300" size={24} />
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">223 6661214</div>
                  <div className="text-gray-400">Si estás en Mar del Plata</div>
                </div>
              </a>

              <a 
                href="tel:2257632222"
                className="flex items-start space-x-4 p-4 bg-slate-800 border border-slate-700 rounded-lg hover:border-gray-600 transition-colors group"
              >
                <div className="bg-gray-700 p-3 rounded-lg group-hover:bg-gray-600 transition-colors">
                  <Phone className="text-gray-300" size={24} />
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">2257 632222</div>
                  <div className="text-gray-400">Si estás en Pinamar, Villa Gesell, Partido de La Costa y Alrededores</div>
                </div>
              </a>

              <a 
                href={`mailto:${studioInfo.email}`}
                className="flex items-start space-x-4 p-4 bg-slate-800 border border-slate-700 rounded-lg hover:border-gray-600 transition-colors group"
              >
                <div className="bg-gray-700 p-3 rounded-lg group-hover:bg-gray-600 transition-colors">
                  <Mail className="text-gray-300" size={24} />
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">Email</div>
                  <div className="text-gray-400">{studioInfo.email}</div>
                </div>
              </a>

              <a 
                href="whatsapp://send?phone=5492236661214&text=Hola%2C%20necesito%20asesoramiento%20sobre%20mi%20caso%20que%20se%20trata%20de..."
                className="flex items-start space-x-4 p-4 bg-green-900/20 border border-green-700/50 rounded-lg hover:border-green-600 transition-colors group"
              >
                <div className="bg-green-600/20 p-3 rounded-lg group-hover:bg-green-600/30 transition-colors">
                  <Phone className="text-green-400" size={24} />
                </div>
                <div className="flex-1">
                  <div className="text-white font-semibold mb-1">WhatsApp</div>
                  <div className="text-gray-400 mb-2">Consulta rápida por WhatsApp</div>
                  <div className="inline-flex items-center text-green-400 text-sm font-medium">
                    Iniciar conversación
                    <Send className="ml-2" size={16} />
                  </div>
                </div>
              </a>

              <div className="flex items-start space-x-4 p-4 bg-slate-800 border border-slate-700 rounded-lg">
                <div className="bg-gray-700 p-3 rounded-lg">
                  <MapPin className="text-gray-300" size={24} />
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">Zonas de Atención</div>
                  <div className="text-gray-400">La Costa • Pinamar • Cariló • Gesell • Mar del Plata</div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="rounded-xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1768839719921-6a554fb3e847"
                alt="Legal Services"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              Envíenos un Mensaje
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-300 font-medium mb-2">
                  Nombre Completo *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-slate-900 border-slate-600 text-white focus:border-blue-500"
                  placeholder="Su nombre"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-300 font-medium mb-2">
                  Email *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-slate-900 border-slate-600 text-white focus:border-blue-500"
                  placeholder="su@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-gray-300 font-medium mb-2">
                  Teléfono
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-slate-900 border-slate-600 text-white focus:border-blue-500"
                  placeholder="Su número de teléfono"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-300 font-medium mb-2">
                  Mensaje *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="bg-slate-900 border-slate-600 text-white focus:border-blue-500 resize-none"
                  placeholder="Describa brevemente su consulta legal..."
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white text-lg py-6"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Consulta'}
                <Send className="ml-2" size={20} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
